import React from "react";
import { X, UserPlus } from "lucide-react";
import Avatar from "./Avatar";
import { displayPhotoUrl } from "@/lib/photo";
import { useTheme } from "../ThemeProvider";
import { getSeliseCSuiteColor } from "./seliseColors";
import LinksDisplay from "./LinksDisplay";

const ROLE_LABELS = { VP: "Vice President" };
const formatRoleLevel = (role) => (role ? (ROLE_LABELS[role] || role) : null);

export default function PersonDetailModal({ person, cSuiteNodes, divisionNodes, allNodes, onView, onClose }) {
  const { theme } = useTheme();
  if (!person) return null;
  const assignedCLevel = cSuiteNodes?.find((c) => c.id === person.c_level_id);
  const isBoard = person.node_type === "board" || person.node_type === "delegate";
  const isCSuite = person.node_type === "c_suite";
  const isPerson = person.node_type === "person";
  const assignedPeople = isCSuite && allNodes
    ? allNodes.filter((n) => n.node_type === "person" && n.c_level_id === person.id)
        .sort((a, b) => (a.order || 0) - (b.order || 0))
    : [];
  const parentDivision = isPerson ? divisionNodes?.find((d) => d.id === person.parent_id) : null;
  const collaborators = isPerson && allNodes
    ? allNodes.filter((n) => person.collaborators?.includes(n.id) && n.node_type === "person")
    : [];
  const ledPeople = allNodes
    ? allNodes.filter((n) => n.node_type === "person" && n.collaborators?.includes(person.id))
        .sort((a, b) => (a.order || 0) - (b.order || 0))
    : [];
  const groupLabel = isBoard
    ? "Board of Directors"
    : isCSuite
    ? "Board of Executives"
    : parentDivision
    ? `${parentDivision.department} [${parentDivision.department_code}]`
    : null;
  const displayTitle = isBoard
    ? person.title
    : isCSuite
    ? (person.full_title || person.title)
    : formatRoleLevel(person.role_level);

  return (
    <div
      className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-xl shadow-xl w-full max-w-lg overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-end p-2">
          <button onClick={onClose} title="Close" className="text-gray-400 hover:text-gray-600">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="flex flex-col items-center px-6 pb-6">
          {person.is_vacancy ? (
            <div className="w-40 h-40 rounded-full border-4 border-dashed border-gray-300 bg-gray-50 flex items-center justify-center shadow-md mb-4">
              <UserPlus className="w-12 h-12 text-gray-400" />
            </div>
          ) : (
            <div className="w-40 h-40 rounded-full overflow-hidden border-4 border-gray-100 shadow-md mb-4">
              {displayPhotoUrl(person.photo_url) ? (
                <img
                  src={displayPhotoUrl(person.photo_url)}
                  alt={person.name}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <Avatar name={person.name} photoUrl={null} size="lg" />
                </div>
              )}
            </div>
          )}
          <h3 className={`text-lg font-bold text-center ${person.is_vacancy ? "italic text-gray-400" : "text-gray-900"}`}>
            {person.is_vacancy ? "Open Position" : person.name}
          </h3>
          {displayTitle && (
            <p className="text-sm font-medium text-gray-600 mt-0.5">{displayTitle}</p>
          )}
          {groupLabel && (
            <p className="text-xs font-semibold text-blue-600 bg-blue-50 px-2.5 py-1 rounded-full mt-1">
              {groupLabel}
            </p>
          )}
          <LinksDisplay links={person.links} />
          <div className="mt-4 w-full space-y-3 text-sm">
            {person.function_area && (
              <div className="flex justify-between">
                <span className="text-gray-500">Function Area</span>
                <span className="font-medium text-gray-900">{person.function_area}</span>
              </div>
            )}
            {person.seniority && (
              <div className="flex justify-between">
                <span className="text-gray-500">Seniority</span>
                <span className="font-medium text-gray-900">{person.seniority}</span>
              </div>
            )}
            {collaborators.length > 0 && (
              <div className="flex justify-between items-center">
                <span className="text-gray-500">Assigned Functional Leaders</span>
                <span className="flex flex-wrap justify-end gap-1 max-w-[60%]">
                  {collaborators.map((c) => (
                    <button
                      key={c.id}
                      onClick={() => onView?.(c)}
                      title={c.name}
                      className="hover:opacity-80 transition-opacity"
                    >
                      <Avatar name={c.name} photoUrl={c.photo_url} size="xs" />
                    </button>
                  ))}
                </span>
              </div>
            )}
            {assignedCLevel && (
              <div className="flex justify-between items-center">
                <span className="text-gray-500">Assigned C-Level</span>
                <button
                  onClick={() => onView?.(assignedCLevel)}
                  className="flex items-center gap-1.5 font-medium text-gray-900 hover:text-blue-600"
                >
                  {assignedCLevel.title}, {assignedCLevel.name}
                  <Avatar name={assignedCLevel.name} photoUrl={assignedCLevel.photo_url} size="xs" />
                </button>
              </div>
            )}
          </div>
          {isCSuite && assignedPeople.length > 0 && (
            <div className="mt-5 w-full">
              <div className="border-t border-gray-200" />
              <p className="text-xs font-bold uppercase tracking-wide text-gray-400 mt-2 mb-2">
                C-Level Leader For ({assignedPeople.length})
              </p>
              <div className="grid grid-cols-2 gap-2 max-h-[130px] overflow-y-auto">
                {assignedPeople.map((p) => {
                  const div = divisionNodes?.find((d) => d.id === p.parent_id);
                  return (
                    <button
                      key={p.id}
                      onClick={() => onView?.(p)}
                      className="flex items-center gap-2 p-2 rounded-lg border border-gray-200 hover:bg-gray-50 hover:border-gray-300 transition-colors text-left"
                    >
                      <Avatar name={p.name} photoUrl={p.photo_url} size="sm" />
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-medium text-gray-900 truncate">{p.name}</p>
                        <p className="text-[10px] text-gray-500 truncate">
                          {formatRoleLevel(p.role_level)}{div ? ` · ${div.department}` : ""}
                        </p>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}
          {ledPeople.length > 0 && (
            <div className="mt-5 w-full">
              <div className="border-t border-gray-200" />
              <p className="text-xs font-bold uppercase tracking-wide text-gray-400 mt-2 mb-2">
                Functional Leader For ({ledPeople.length})
              </p>
              <div className="grid grid-cols-2 gap-2 max-h-[130px] overflow-y-auto">
                {ledPeople.map((p) => {
                  const div = divisionNodes?.find((d) => d.id === p.parent_id);
                  return (
                    <button
                      key={p.id}
                      onClick={() => onView?.(p)}
                      className="flex items-center gap-2 p-2 rounded-lg border border-gray-200 hover:bg-gray-50 hover:border-gray-300 transition-colors text-left"
                    >
                      <Avatar name={p.name} photoUrl={p.photo_url} size="sm" />
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-medium text-gray-900 truncate">{p.name}</p>
                        <p className="text-[10px] text-gray-500 truncate">
                          {formatRoleLevel(p.role_level)}{div ? ` · ${div.department}` : ""}
                        </p>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}